import { useGoals } from 'features/goals';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { TaskDetailDialog } from 'shared/dialogs/task-detail';
import { useActivePath } from 'shared/hooks/use-active-path';
import { usePlatformShortcut } from 'shared/hooks/use-platform-shortcut';
import { Paths } from 'shared/routes/paths';
import { ID } from 'shared/types/id';
import { CreateTask, Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { MetaViewOptions } from 'shared/views/task-detail';
import { useTheme } from 'styled-components';

import { DiscardChangesDialog } from './discard-changes-dialog';
import { getTimeslotDetailUrl } from './get-timeslot-detail-url';
import * as Styled from './task-detail.style';
import {
  TaskDetailActionsContext,
  TaskDetailActionsContextProps,
  TaskDetailContext,
  TaskDetailContextProps,
} from './task-detail-context';

export type TaskDetailProviderProps = {
  children: React.ReactNode;
};

export const TaskDetailProvider: React.FC<TaskDetailProviderProps> = ({
  children,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const activePath = useActivePath({ level: 2 });
  const { id: activePathId } = useParams();
  const goals = useGoals();

  const closeTimerRef = useRef<number>();
  const hasUnsavedCreateContentRef = useRef(false);
  const [initialValues, setInitialValues] = useState<
    Partial<CreateTask> | undefined
  >(undefined);
  const [dateAnchored, setDateAnchored] = useState(false);
  const [activeTask, setActiveTask] = useState<ID>();
  const [initialView, setInitialView] = useState(MetaViewOptions.Settings);
  const [isDiscardDialogOpen, setDiscardDialogOpen] = useState(false);

  const navigateToTimeslotDetail = useCallback(
    (task: Task | undefined): boolean => {
      const url = getTimeslotDetailUrl(task, goals);
      if (!url) {
        return false;
      }
      navigate(url);
      return true;
    },
    [goals, navigate],
  );

  const openTask = useCallback(
    (task: Task | undefined, view?: MetaViewOptions) => {
      if (navigateToTimeslotDetail(task)) {
        return;
      }

      closeTimerRef.current && window.clearTimeout(closeTimerRef.current);
      setActiveTask(task?.id);
      setInitialView(
        view ??
          (task?.type === TaskType.Habit
            ? MetaViewOptions.Statistics
            : MetaViewOptions.Settings),
      );
      setInitialValues(undefined);
      setDateAnchored(false);

      if (theme.isMobile) {
        document.body.style.overflow = task ? 'hidden' : '';
      }
    },
    [navigateToTimeslotDetail, theme.isMobile],
  );

  const openTaskStatistics = useCallback(
    (task: Task | undefined) => {
      if (navigateToTimeslotDetail(task)) {
        return;
      }

      closeTimerRef.current && window.clearTimeout(closeTimerRef.current);
      setActiveTask(task?.id);
      setInitialView(MetaViewOptions.Statistics);
      setInitialValues(undefined);
      setDateAnchored(false);
    },
    [navigateToTimeslotDetail],
  );

  const openCreateTask = useCallback(
    (
      initialValues?: Partial<CreateTask>,
      options?: { dateAnchored?: boolean },
    ) => {
      setInitialValues(initialValues ?? {});
      setDateAnchored(!!options?.dateAnchored);
      setActiveTask(undefined);
    },
    [],
  );

  const performClose = useCallback(() => {
    hasUnsavedCreateContentRef.current = false;
    setActiveTask(undefined);
    setInitialValues(undefined);
    setDateAnchored(false);
    document.body.style.overflow = '';
  }, []);

  const closeDetail = useCallback(() => {
    if (hasUnsavedCreateContentRef.current) {
      setDiscardDialogOpen(true);
      return;
    }
    performClose();
  }, [performClose]);

  const cancelDiscard = useCallback(() => {
    setDiscardDialogOpen(false);
  }, []);

  const confirmDiscard = useCallback(() => {
    setDiscardDialogOpen(false);
    performClose();
  }, [performClose]);

  const setHasUnsavedCreateContent = useCallback((has: boolean) => {
    hasUnsavedCreateContentRef.current = has;
  }, []);

  const openCreateFromShortcut = useCallback(() => {
    const initialProps: Partial<CreateTask> = {};

    switch (activePath) {
      // case Paths.Home:
      //   initialProps.endStrategy = { deadline: today, completionCount: null };
      //   break;
      case Paths.GoalDetail:
      case Paths.ProjectDetail:
        initialProps.goalId = activePathId as ID;
        break;
      case Paths.LifeAreaDetail:
        initialProps.lifeAreaId = activePathId as ID;
        break;
      case Paths.TagDetail:
        initialProps.tagIds = [activePathId as ID];
        break;
    }

    openCreateTask(initialProps);
  }, [activePath, activePathId, openCreateTask]);

  const [isSubmenuOpen, setSubmenuOpen] = useState(false);
  const [isCoveredByDialog, setIsCoveredByDialog] = useState(false);

  const actionsValue = useMemo<TaskDetailActionsContextProps>(
    () => ({
      openTaskDetail: openTask,
      openTaskStatistics: openTaskStatistics,
      openCreateTask,
      closeDetail,
    }),
    [closeDetail, openCreateTask, openTask, openTaskStatistics],
  );

  const value = useMemo<TaskDetailContextProps>(
    () => ({
      activeTaskId: activeTask,
      isSubmenuOpen,
      setSubmenuOpen,
      setHasUnsavedCreateContent,
      isCoveredByDialog,
      setIsCoveredByDialog,
    }),
    [activeTask, isCoveredByDialog, isSubmenuOpen, setHasUnsavedCreateContent],
  );

  usePlatformShortcut('KeyN', openCreateFromShortcut);

  return (
    <TaskDetailActionsContext.Provider value={actionsValue}>
      <TaskDetailContext.Provider value={value}>
        <Styled.Container>
          {children}

          <TaskDetailDialog
            taskId={activeTask}
            onClose={closeDetail}
            initialValues={initialValues}
            dateAnchored={dateAnchored}
            openOnView={initialView}
          />
          <DiscardChangesDialog
            open={isDiscardDialogOpen}
            onClose={cancelDiscard}
            onConfirm={confirmDiscard}
          />
        </Styled.Container>
      </TaskDetailContext.Provider>
    </TaskDetailActionsContext.Provider>
  );
};
